import React, { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { fetchMyWrapped } from "../lib/api";

/**
 * SeasonWrappedModal — Spotify-Wrapped-style year-end recap for a single
 * player. PRO-only (backend enforces 402). Renders 5 scrollable cards plus
 * one shareable "summary" card with a PNG export button.
 */
export function SeasonWrappedModal({ open, year, onClose, onToast }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [savingPng, setSavingPng] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setData(null); setErr("");
    fetchMyWrapped(year)
      .then(setData)
      .catch((e) => {
        const detail = e?.response?.data?.detail || "Couldn't load Wrapped.";
        setErr(detail);
      });
  }, [open, year]);

  if (!open) return null;

  const downloadPng = async () => {
    if (!cardRef.current) return;
    setSavingPng(true);
    try {
      const png = await toPng(cardRef.current, {
        cacheBust: true, pixelRatio: 2, backgroundColor: "#0a0a1f",
      });
      const a = document.createElement("a");
      a.href = png;
      a.download = `league-hub-wrapped-${data?.year || "season"}.png`;
      a.click();
      onToast?.("✨ Wrapped card saved to your downloads.");
    } catch (e) {
      onToast?.("Couldn't render the PNG. Try again?");
    } finally { setSavingPng(false); }
  };

  return (
    <div className="fixed inset-0 z-[96] bg-black/90 backdrop-blur-md p-4 overflow-y-auto"
         onClick={onClose}
         data-testid="wrapped-backdrop">
      <div className="max-w-xl mx-auto my-8" onClick={(e) => e.stopPropagation()}>
        {err ? (
          <div className="glass rounded-3xl p-6 text-center" data-testid="wrapped-error">
            <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold mb-1">
              <i className="fas fa-lock mr-1" />WRAPPED — PRO ONLY
            </p>
            <p className="text-sm text-white/75 mt-3">{err}</p>
            <button onClick={onClose} className="mt-5 btn-ghost px-4 py-2 rounded-xl text-[11px] tracking-widest">CLOSE</button>
          </div>
        ) : !data ? (
          <p className="text-center text-white/45 py-12">Loading your year…</p>
        ) : (
          <>
            <div ref={cardRef}
                 data-testid="wrapped-card"
                 className="rounded-3xl p-7 bg-gradient-to-br from-[#0b0a2a] via-[#19073a] to-[#250944] border-2 border-[#14b8a6]/40 shadow-[0_30px_120px_rgba(20, 184, 166,0.35)]">
              <div className="text-center">
                <p className="text-[10px] tracking-[4px] text-[#a3a3ac] font-bold">
                  LEAGUE HUB · WRAPPED · {data.year}
                </p>
                <h2 className="font-display text-4xl mt-1"
                    style={{ background: "linear-gradient(90deg,#14b8a6 0%,#14b8a6 50%,#14b8a6 100%)", WebkitBackgroundClip: "text", color: "transparent" }}>
                  {data.user_name?.toUpperCase() || "OPERATOR"}
                </h2>
              </div>

              {data.matches_played === 0 ? (
                <p className="text-center text-white/55 mt-8 text-sm" data-testid="wrapped-empty">
                  No matches recorded in {data.year}. Play some matches to unlock your Wrapped recap.
                </p>
              ) : (
                <>
                  {/* Headline KPIs */}
                  <div className="grid grid-cols-3 gap-3 mt-6 text-center">
                    <Stat label="MATCHES" value={data.matches_played} color="cyan" />
                    <Stat label="WINS"    value={data.wins} color="green" />
                    <Stat label="WIN %"   value={`${data.win_rate}%`} color="violet" />
                  </div>

                  {/* MMR journey */}
                  <div className="mt-6 rounded-2xl p-4 bg-black/30 border border-[#14b8a6]/25">
                    <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">MMR JOURNEY</p>
                    <div className="flex items-end gap-4 mt-2">
                      <p className="font-display text-3xl tabular-nums text-white">{data.mmr_end}</p>
                      <p className={`text-sm font-bold ${data.mmr_delta >= 0 ? "text-[#14b8a6]" : "text-[#14b8a6]"}`}>
                        {data.mmr_delta >= 0 ? "▲" : "▼"} {Math.abs(data.mmr_delta)}
                      </p>
                      <p className="text-[10px] text-white/45 ml-auto">started @ {data.mmr_start}</p>
                    </div>
                    <MmrSpark series={data.mmr_series || []} />
                  </div>

                  {/* Best streak */}
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div className="rounded-2xl p-3 bg-[#14b8a6]/10 border border-[#14b8a6]/30">
                      <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">BEST STREAK</p>
                      <p className="font-display text-3xl text-white tabular-nums mt-1">{data.best_win_streak} 🔥</p>
                    </div>
                    <div className="rounded-2xl p-3 bg-[#14b8a6]/10 border border-[#14b8a6]/30">
                      <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">LEAGUES PLAYED</p>
                      <p className="font-display text-3xl text-white tabular-nums mt-1">{data.leagues_count}</p>
                    </div>
                  </div>

                  {/* Top opponent / league */}
                  {data.top_opponent && (
                    <p className="mt-4 text-center text-sm text-white/85" data-testid="wrapped-top-opp">
                      Most-played opponent — <span className="text-[#14b8a6] font-bold">{data.top_opponent.name}</span> ({data.top_opponent.matches}×)
                    </p>
                  )}
                  {data.top_league?.name && (
                    <p className="mt-1 text-center text-sm text-white/85">
                      Home league — <span className="text-[#14b8a6] font-bold">{data.top_league.name}</span>
                    </p>
                  )}
                  {data.biggest_win && (
                    <div className="mt-4 rounded-2xl p-3 bg-black/30 border border-white/10 text-center">
                      <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">BIGGEST W</p>
                      <p className="text-sm text-white mt-1">
                        <span className="tabular-nums font-bold">{data.biggest_win.score_mine}-{data.biggest_win.score_opp}</span>
                        <span className="text-white/55"> vs </span>
                        <span className="font-bold">{data.biggest_win.opp_name}</span>
                      </p>
                    </div>
                  )}

                  <p className="mt-6 text-center text-[10px] tracking-[3px] text-white/40">
                    LEAGUE HUB · @{(data.user_name || "you").toLowerCase().replace(/\s/g, "")} · {data.year}
                  </p>
                </>
              )}
            </div>

            <div className="mt-5 flex flex-wrap justify-center gap-2">
              <button type="button" onClick={onClose}
                      data-testid="wrapped-close"
                      className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-ghost">
                CLOSE
              </button>
              {data.matches_played > 0 && (
                <button type="button" onClick={downloadPng} disabled={savingPng}
                        data-testid="wrapped-download-btn"
                        className="text-[11px] tracking-widest px-5 py-2 rounded-xl btn-neon disabled:opacity-50">
                  {savingPng ? "…" : <><i className="fas fa-download mr-1" />DOWNLOAD PNG</>}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  const palette = {
    cyan:   "text-[#14b8a6] border-[#14b8a6]/40 bg-[#14b8a6]/10",
    green:  "text-[#14b8a6] border-[#14b8a6]/40 bg-[#14b8a6]/10",
    violet: "text-[#14b8a6] border-[#14b8a6]/40 bg-[#14b8a6]/10",
  }[color];
  return (
    <div className={`rounded-2xl p-3 border ${palette}`}>
      <p className="text-[10px] tracking-[3px] font-bold">{label}</p>
      <p className="font-display text-2xl text-white tabular-nums mt-0.5">{value}</p>
    </div>
  );
}

function MmrSpark({ series }) {
  if (!series || series.length < 2) {
    return <p className="text-[10px] text-white/35 mt-1">Play matches to draw your MMR journey.</p>;
  }
  const W = 480, H = 60, pad = 4;
  const xs = series.map((_, i) => i);
  const ys = series.map((p) => p.mmr || 1200);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const range = Math.max(1, maxY - minY);
  const pts = xs.map((x, i) => {
    const px = pad + (x / Math.max(1, xs.length - 1)) * (W - pad * 2);
    const py = H - pad - ((ys[i] - minY) / range) * (H - pad * 2);
    return `${px.toFixed(1)},${py.toFixed(1)}`;
  });
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="mt-2 w-full h-12">
      <defs>
        <linearGradient id="mmrGrad" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#14b8a6" />
          <stop offset="100%" stopColor="#14b8a6" />
        </linearGradient>
      </defs>
      <polyline fill="none" stroke="url(#mmrGrad)" strokeWidth="2.5" points={pts.join(" ")} />
    </svg>
  );
}

export default SeasonWrappedModal;
